import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, CheckCircle2, MoreHorizontal, Plus } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Banner } from '../../components/Banner';
import { Input } from '../../components/Input';
import { Tabs } from '../../components/Tabs';
import { Drawer } from '../../components/Drawer';
import { Money } from '../../components/Money';
import { useAdminPackage, updateAdminPackage, addPackagePrice, setPackageAddOns, type AdminPackageDetail } from '../../api/admin';
import { useAddons } from '../../api/catalog';

const EDITOR_STATUS_TONE: Record<string, 'success' | 'info' | 'warning' | 'neutral'> = {
  published: 'success', draft: 'info', deprecated: 'warning', archived: 'neutral',
};
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Package editor — General / Prices / Conditions / Modules / Add-ons, all persisted. */
export function PackageEditor() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const { data: detail, isLoading } = useAdminPackage(id ?? '');

  if (isLoading || !detail) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-[1440px] mx-auto space-y-4" aria-busy="true">
        <div className="skeleton h-8 w-64 max-w-full rounded" />
        <div className="space-y-3"><div className="skeleton h-14 rounded-lg" /><div className="skeleton h-14 rounded-lg" /><div className="skeleton h-14 rounded-lg" /></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <Link to="/admin/packages" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 mb-4 transition">
        <ArrowLeft className="w-4 h-4" />
        All packages
      </Link>

      <Card className="p-5 sm:p-6 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1">
              Edit package · {detail.ownedBy === 'platform' ? 'Platform-owned' : 'Reseller-owned'}
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 flex items-center gap-3">
              {detail.name}
              <Badge tone={EDITOR_STATUS_TONE[detail.status] ?? 'neutral'}>{cap(detail.status)}</Badge>
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button>
          </div>
        </div>
      </Card>

      <Card className="px-5 sm:px-6 pt-2 pb-5 sm:pb-6">
        <Tabs
          tabs={[
            { id: 'general', label: 'General', content: <GeneralTab id={detail.id} detail={detail} /> },
            { id: 'prices', label: 'Prices', content: <PricesTab id={detail.id} prices={detail.prices} />, count: detail.prices.length },
            { id: 'conditions', label: 'Conditions', content: <ConditionsTab id={detail.id} detail={detail} /> },
            { id: 'modules', label: 'Modules', content: <ModulesTab id={detail.id} detail={detail} /> },
            { id: 'addons', label: 'Add-ons', content: <AddonsTab id={detail.id} /> },
          ]}
        />
      </Card>
    </div>
  );
}

function GeneralTab({ id, detail }: { id: string; detail: AdminPackageDetail }) {
  const qc = useQueryClient();
  const [name, setName] = useState(detail.name);
  const code = detail.internalCode;
  const [tagline, setTagline] = useState(detail.tagline);
  const [description, setDescription] = useState(detail.description);
  const [badge, setBadge] = useState(detail.marketingBadge);
  const [status, setStatus] = useState<'published' | 'draft' | 'deprecated' | 'archived'>(detail.status);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!name.trim()) { setError('Name cannot be empty.'); return; }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await updateAdminPackage(id, { name: name.trim(), tagline, description, marketingBadge: badge, status });
      await qc.invalidateQueries({ queryKey: ['admin', 'package', id] });
      await qc.invalidateQueries({ queryKey: ['admin', 'allPackages'] });
      await qc.invalidateQueries({ queryKey: ['packages'] });
      setSaved(true);
    } catch {
      setError('Could not save changes.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5 max-w-2xl">
      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">{error}</div>
      )}
      <Input label="Name" required value={name} onChange={(e) => { setName(e.target.value); setSaved(false); }} />
      <Input label="Internal code" value={code} disabled hint="Immutable; used by webhooks" />
      <Input label="Tagline" value={tagline} onChange={(e) => setTagline(e.target.value)} />
      <div>
        <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand resize-none"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Marketing badge</label>
        <select
          value={badge}
          onChange={(e) => setBadge(e.target.value)}
          className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand"
        >
          <option value="">— None —</option>
          <option value="most-popular">Most popular</option>
          <option value="best-value">Best value</option>
          <option value="new">New</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Status</label>
        <div className="space-y-2">
          {(['published', 'draft', 'deprecated', 'archived'] as const).map((s) => (
            <label key={s} className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
              <input
                type="radio"
                name="status"
                checked={status === s}
                onChange={() => setStatus(s)}
                className="text-brand focus:ring-brand"
              />
              <span className="capitalize">{s}</span>
            </label>
          ))}
        </div>
      </div>

      <Banner tone="warning">
        Changing status from <strong>Published</strong> to <strong>Deprecated</strong> blocks new sign-ups for this package.
        Existing subscribers are unaffected.
      </Banner>

      <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700 flex items-center justify-end gap-3">
        {saved && (
          <span className="text-sm text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4" /> Saved
          </span>
        )}
        <Button onClick={save} loading={saving} disabled={saving}>Save changes</Button>
      </div>
    </div>
  );
}

function PricesTab({ id, prices }: { id: string; prices: AdminPackageDetail['prices'] }) {
  const qc = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
          Prices <span className="text-zinc-500 dark:text-zinc-400 tabular-nums">({prices.length})</span>
        </h3>
        <Button size="sm" iconLeft={<Plus className="w-4 h-4" />} onClick={() => setDrawerOpen(true)}>Add price</Button>
      </div>

      <div className="overflow-x-auto nice-scroll -mx-6">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
            <tr>
              <th className="text-left font-medium px-6 py-3">Currency</th>
              <th className="text-left font-medium px-4 py-3">Interval</th>
              <th className="text-right font-medium px-4 py-3">Amount</th>
              <th className="text-right font-medium px-4 py-3">Trial</th>
              <th className="text-left font-medium px-4 py-3">Status</th>
              <th className="text-right font-medium px-6 py-3">On subs</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
            {prices.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-8 text-center text-sm text-zinc-500 dark:text-zinc-400">No prices yet.</td></tr>
            )}
            {prices.map((p) => (
              <tr key={p.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                <td className="px-6 py-3 font-medium text-zinc-950 dark:text-zinc-50">{p.currency}</td>
                <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200 capitalize">{p.interval === 'month' ? 'Monthly' : p.interval === 'year' ? 'Yearly' : p.interval}</td>
                <td className="px-4 py-3 text-right">
                  <Money minor={p.amount} currencyCode={p.currency} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-zinc-700 dark:text-zinc-200">{p.trialDays} d</td>
                <td className="px-4 py-3">
                  <Badge tone={p.status === 'active' ? 'success' : p.status === 'draft' ? 'info' : 'neutral'}>
                    {p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                  </Badge>
                </td>
                <td className="px-6 py-3 text-right tabular-nums text-zinc-700 dark:text-zinc-200">{p.subscribersOn}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Banner tone="info" className="mt-5">
        Existing subscribers stay on their current price. To change a price, add a new price
        here — new subscribers get it immediately; existing ones keep their current price.
      </Banner>

      <AddPriceDrawer
        open={drawerOpen}
        packageId={id}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => {
          void qc.invalidateQueries({ queryKey: ['admin', 'package', id] });
          void qc.invalidateQueries({ queryKey: ['prices', 'byPackage'] });
          setDrawerOpen(false);
        }}
      />
    </div>
  );
}

function AddPriceDrawer({ open, packageId, onClose, onSaved }: { open: boolean; packageId: string; onClose: () => void; onSaved: () => void }) {
  const [interval, setInterval] = useState<'month' | 'year' | 'quarter' | 'one_time'>('month');
  const [amount, setAmount] = useState('');
  const [trial, setTrial] = useState('0');
  const [proration, setProration] = useState<'prorate' | 'full'>('prorate');
  const [anchor, setAnchor] = useState<'start' | 'first'>('start');
  const [status, setStatus] = useState<'draft' | 'active'>('draft');
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const amountNum = Number(amount);
  const trialNum = Number(trial);
  const valid = Number.isFinite(amountNum) && amountNum > 0 && Number.isInteger(trialNum) && trialNum >= 0;

  async function handleSave() {
    if (!valid || submitting) return;
    setSubmitting(true);
    setSaveError(null);
    try {
      await addPackagePrice(packageId, {
        currencyCode: 'BDT',
        interval,
        // BDT input → minor units.
        amountMinor: Math.round(amountNum * 100),
        trialDays: trialNum,
        status,
        prorationEnabled: proration === 'prorate',
        billingAnchor: anchor === 'first' ? 'first_of_month' : 'subscription_start',
      });
      onSaved();
      setAmount('');
      setTrial('0');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save the price. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Add a new price"
      width="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSave} loading={submitting} disabled={!valid}>Save price</Button>
        </>
      }
    >
      <div className="space-y-5">
        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Interval</span>
          <div className="grid grid-cols-2 gap-2">
            {(['month', 'year', 'quarter', 'one_time'] as const).map((i) => (
              <label key={i} className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded-md border border-zinc-200 dark:border-zinc-600 hover:bg-zinc-50 dark:hover:bg-zinc-800">
                <input type="radio" name="interval" checked={interval === i} onChange={() => setInterval(i)} className="text-brand focus:ring-brand" />
                <span className="capitalize">{i.replace('_', '-')}</span>
              </label>
            ))}
          </div>
        </div>
        <Input
          label="Amount (BDT)"
          required
          prefix={<span className="text-sm">৳</span>}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <Input label="Trial" value={trial} onChange={(e) => setTrial(e.target.value)} suffix={<span className="text-xs">days</span>} hint="0 = no trial" />

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Billing cycle anchor</span>
          <div className="space-y-2">
            <Radio label="Subscription start date" checked={anchor === 'start'} onChange={() => setAnchor('start')} name="anchor" />
            <Radio label="1st of month" checked={anchor === 'first'} onChange={() => setAnchor('first')} name="anchor" />
          </div>
        </div>

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Proration</span>
          <div className="space-y-2">
            <Radio label="Prorate when changing plans" checked={proration === 'prorate'} onChange={() => setProration('prorate')} name="proration" />
            <Radio label="Charge full new price; ignore unused" checked={proration === 'full'} onChange={() => setProration('full')} name="proration" />
          </div>
        </div>

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Status</span>
          <div className="space-y-2">
            <Radio label="Draft" checked={status === 'draft'} name="price-status" onChange={() => setStatus('draft')} />
            <Radio label="Active" checked={status === 'active'} name="price-status" onChange={() => setStatus('active')} />
          </div>
        </div>

        {saveError && (
          <Banner tone="error" title="Couldn’t save the price">
            {saveError}
          </Banner>
        )}
      </div>
    </Drawer>
  );
}

function ConditionsTab({ id, detail }: { id: string; detail: AdminPackageDetail }) {
  const qc = useQueryClient();
  const limStr = (v: number | 'unlimited') => (v === 'unlimited' ? '' : String(v));
  const [locations, setLocations] = useState(limStr(detail.limits.locations));
  const [users, setUsers] = useState(limStr(detail.limits.users));
  const [products, setProducts] = useState(limStr(detail.limits.products));
  const [invoices, setInvoices] = useState(limStr(detail.limits.invoices));
  const [invoicesYearly, setInvoicesYearly] = useState(limStr(detail.limits.invoicesYearly ?? detail.limits.invoices));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toLimit = (s: string): number | 'unlimited' => (s.trim() === '' ? 'unlimited' : Number(s));

  async function save() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await updateAdminPackage(id, {
        limits: { locations: toLimit(locations), users: toLimit(users), products: toLimit(products), invoices: toLimit(invoices), invoicesYearly: toLimit(invoicesYearly) },
      });
      await qc.invalidateQueries({ queryKey: ['admin', 'package', id] });
      await qc.invalidateQueries({ queryKey: ['admin', 'allPackages'] });
      setSaved(true);
    } catch {
      setError('Could not save the limits.');
    } finally {
      setSaving(false);
    }
  }

  const set = (fn: (v: string) => void) => (v: string) => { fn(v); setSaved(false); };

  return (
    <div className="space-y-6 max-w-2xl">
      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">{error}</div>
      )}
      <section>
        <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1">Resource limits enforced on the POS</h3>
        <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-3">Leave a field blank for unlimited.</p>
        <div className="space-y-3">
          <LimitRow label="Locations" value={locations} onChange={set(setLocations)} />
          <LimitRow label="Users" value={users} onChange={set(setUsers)} />
          <LimitRow label="Products" value={products} onChange={set(setProducts)} />
          <LimitRow label="Invoices / month" value={invoices} onChange={set(setInvoices)} />
          <LimitRow label="Invoices / year" value={invoicesYearly} onChange={set(setInvoicesYearly)} />
        </div>
      </section>

      <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700 flex items-center justify-end gap-3">
        {saved && (
          <span className="text-sm text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Saved</span>
        )}
        <Button onClick={save} loading={saving} disabled={saving}>Save changes</Button>
      </div>
    </div>
  );
}

function LimitRow({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="grid sm:grid-cols-[1fr_160px] gap-3 items-center">
      <label className="text-sm text-zinc-700 dark:text-zinc-200">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Unlimited"
        className="px-3 py-2 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 tabular-nums"
      />
    </div>
  );
}

const MODULE_GROUPS: { title: string; modules: string[] }[] = [
  { title: 'Core', modules: ['POS sale', 'Purchases', 'Inventory', 'Stock transfers', 'Stock adjustment', 'Income', 'Expenses', 'Account'] },
  { title: 'Modules', modules: ['Subscription', 'Services', 'Service staff', 'Types of service', 'Manufacturing', 'Tables', 'Modifiers', 'Booking', 'Kitchen', 'WooCommerce', 'Essentials/HRM', 'CRM', 'Repair', 'RMA', 'Support', 'Superadmin'] },
];

function ModulesTab({ id, detail }: { id: string; detail: AdminPackageDetail }) {
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState<Set<string>>(() => new Set(detail.modules));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggle(m: string) {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(m)) next.delete(m); else next.add(m);
      return next;
    });
    setSaved(false);
  }

  async function save() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await updateAdminPackage(id, { modules: [...enabled] });
      await qc.invalidateQueries({ queryKey: ['admin', 'package', id] });
      setSaved(true);
    } catch {
      setError('Could not save the modules.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">{error}</div>
      )}
      {MODULE_GROUPS.map((g) => (
        <section key={g.title}>
          <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-3">{g.title}</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {g.modules.map((m) => (
              <label key={m} className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
                <input type="checkbox" checked={enabled.has(m)} onChange={() => toggle(m)} className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand" />
                {m}
              </label>
            ))}
          </div>
        </section>
      ))}
      <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700 flex items-center justify-end gap-3">
        {saved && (
          <span className="text-sm text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Saved</span>
        )}
        <Button onClick={save} loading={saving} disabled={saving}>Save changes</Button>
      </div>
    </div>
  );
}

function AddonsTab({ id }: { id: string }) {
  const qc = useQueryClient();
  const { data: addons = [], isLoading } = useAddons();
  // Selection seeds from each add-on's real package links once loaded.
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const current =
    selected ??
    new Set(
      addons
        .filter((a) => (a as { compatiblePackageIds?: string[] }).compatiblePackageIds?.includes(id))
        .map((a) => a.id),
    );

  function toggle(addonId: string) {
    const next = new Set(current);
    if (next.has(addonId)) next.delete(addonId);
    else next.add(addonId);
    setSelected(next);
    setSaved(false);
  }

  async function save() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await setPackageAddOns(id, [...current]);
      await qc.invalidateQueries({ queryKey: ['addons'] });
      setSaved(true);
    } catch {
      setError('Could not save the add-on links.');
    } finally {
      setSaving(false);
    }
  }

  if (isLoading) {
    return <div className="text-sm text-zinc-500 dark:text-zinc-400 py-6">Loading add-ons…</div>;
  }

  return (
    <div className="space-y-3 max-w-2xl">
      <p className="text-sm text-zinc-600 dark:text-zinc-300">
        Choose which optional add-ons subscribers can purchase on top of this package.
      </p>
      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">{error}</div>
      )}
      {addons.length === 0 ? (
        <p className="text-sm text-zinc-500 dark:text-zinc-400 py-4">
          No add-ons exist yet — create them under <span className="font-medium">Pricing → Add-ons</span> first.
        </p>
      ) : (
        addons.map((a) => (
          <Card key={a.id} className="p-4 border-zinc-200 dark:border-zinc-700 flex items-center justify-between">
            <label className="flex items-center gap-3 cursor-pointer flex-1">
              <input
                type="checkbox"
                checked={current.has(a.id)}
                onChange={() => toggle(a.id)}
                className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
              />
              <div>
                <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50">{a.name}</div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400">
                  {a.billing === 'one_time' ? 'one-time' : 'recurring'}
                </div>
              </div>
            </label>
            <Money minor={a.amount} hideCents tone="neutral" className="text-sm font-medium text-zinc-950 dark:text-zinc-50" />
          </Card>
        ))
      )}
      <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700 flex items-center justify-end gap-3">
        {saved && (
          <span className="text-sm text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4" /> Saved
          </span>
        )}
        <Button onClick={save} loading={saving} disabled={saving || addons.length === 0}>Save changes</Button>
      </div>
    </div>
  );
}

function Radio({
  name,
  checked,
  onChange,
  label,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
      <input type="radio" name={name} checked={checked} onChange={onChange} className="text-brand focus:ring-brand" />
      {label}
    </label>
  );
}

function Check({ label, defaultChecked }: { label: string; defaultChecked?: boolean }) {
  return (
    <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
      <input type="checkbox" defaultChecked={defaultChecked} className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand" />
      {label}
    </label>
  );
}
